import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import Header from './app/Header';
import Footer from './app/Footer';
import FooterCopyright from './app/FooterCopyright';
import supabase from './utils/supabaseClient';
import { isValidEmail } from './utils/validators';

const TIPOS = [
  { value: 'acesso',                       label: 'Acesso aos meus dados (saber o que vocês têm)' },
  { value: 'correcao',                     label: 'Correção de dados incorretos ou desatualizados' },
  { value: 'portabilidade',                label: 'Portabilidade (receber meus dados em formato estruturado)' },
  { value: 'anonimizacao',                 label: 'Anonimização ou bloqueio de dados desnecessários' },
  { value: 'exclusao',                     label: 'Exclusão da minha conta e anonimização dos meus dados' },
  { value: 'revogacao_consentimento',      label: 'Revogação de consentimento previamente dado' },
  { value: 'informacao_compartilhamento',  label: 'Informação sobre com quem meus dados são compartilhados' },
  { value: 'outro',                        label: 'Outro' },
];

const Lgpd = () => {
  const [form, setForm] = useState({
    tipo: 'acesso',
    email: '',
    whatsapp: '',
    detalhes: '',
    confirmou: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { protocolo }
  const [error, setError] = useState('');

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isValidEmail(form.email)) {
      setError('Informe um e-mail válido.');
      return;
    }
    if (!form.confirmou) {
      setError('Confirme a declaração de veracidade.');
      return;
    }

    setSubmitting(true);
    const { data, error: err } = await supabase
      .from('lgpd_solicitacoes')
      .insert([{
        tipo:      form.tipo,
        email:     form.email.trim(),
        whatsapp:  form.whatsapp.trim() || null,
        detalhes:  form.detalhes.trim() || null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      }])
      .select('id')
      .single();
    setSubmitting(false);

    if (err) {
      console.error(err);
      setError('Erro ao registrar sua solicitação. Tente novamente ou envie diretamente para privacidade@fretz.com.br.');
      return;
    }

    setDone({ protocolo: data.id.slice(0, 8).toUpperCase() });
  };

  if (done) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <section className="container mx-auto px-4 lg:px-6 py-20 max-w-xl">
          <div className="card p-8 text-center">
            <CheckCircleIcon className="h-12 w-12 text-green-600 mx-auto" />
            <h1 className="font-display text-2xl font-bold text-ink-900 mt-4">
              Solicitação registrada
            </h1>
            <p className="mt-3 text-ink-600 leading-relaxed">
              Recebemos seu pedido. Vamos respondê-lo em até <strong>15 dias</strong>,
              conforme a LGPD. Pode ser que entremos em contato pelos canais
              cadastrados para validar sua identidade antes de atender.
            </p>
            <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-50 border border-brand-100 text-sm">
              <span className="text-ink-500">Protocolo</span>
              <span className="font-mono font-bold text-brand-700">{done.protocolo}</span>
            </div>
            <p className="mt-6 text-xs text-ink-400">
              Guarde este protocolo. Para acompanhar, escreva a privacidade@fretz.com.br.
            </p>
            <Link to="/" className="mt-6 btn-primary inline-flex">Voltar para a home</Link>
          </div>
        </section>
        <Footer />
        <FooterCopyright />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 bg-mesh-light opacity-60" aria-hidden="true" />
        <div className="relative container mx-auto px-4 lg:px-6 py-16 lg:py-20 max-w-2xl">
          <span className="badge-brand mb-5">LGPD · Direitos do titular</span>
          <h1 className="font-display text-display-lg text-ink-900">
            Exercer meus direitos de proteção de dados
          </h1>
          <p className="mt-4 text-ink-600 leading-relaxed">
            Você é o titular dos seus dados pessoais e a Lei Geral de Proteção
            de Dados (Lei nº 13.709/2018) garante uma série de direitos.
            Preencha o formulário abaixo para exercer qualquer um deles —
            responderemos em até <strong>15 dias</strong>.
          </p>

          <div className="mt-8 p-5 rounded-2xl bg-brand-50 border border-brand-100 flex items-start gap-3">
            <ShieldCheckIcon className="h-5 w-5 text-brand-700 shrink-0 mt-0.5" />
            <div className="text-sm text-ink-700 leading-relaxed">
              <strong>Importante</strong>: a exclusão dos seus dados é executada
              por meio de <strong>anonimização robusta</strong>. Os dados que
              te identificam (nome, e-mail, WhatsApp etc.) são substituídos
              por valores fictícios irreversíveis. Já registros financeiros e
              fiscais (valor, data, status de PEDIDOS) são mantidos em forma
              anonimizada pelo prazo legal exigido pelo Código Tributário, CDC
              (5 anos) e Marco Civil (12 meses), conforme o art. 16, I e IV,
              da LGPD. Saiba mais na{' '}
              <Link to="/privacidade#exclusao" className="text-brand-700 underline">
                Política de Privacidade
              </Link>.
            </div>
          </div>

          <form onSubmit={submit} className="mt-8 card p-6 space-y-5">
            <div>
              <label className="label" htmlFor="lgpd-tipo">Qual direito você quer exercer?</label>
              <select
                id="lgpd-tipo"
                className="input"
                value={form.tipo}
                onChange={(e) => update('tipo', e.target.value)}
              >
                {TIPOS.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="label" htmlFor="lgpd-email">E-mail usado no cadastro</label>
                <input
                  id="lgpd-email"
                  className="input"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  placeholder="voce@email.com"
                />
              </div>
              <div>
                <label className="label" htmlFor="lgpd-wpp">WhatsApp (opcional)</label>
                <input
                  id="lgpd-wpp"
                  className="input"
                  type="text"
                  value={form.whatsapp}
                  onChange={(e) => update('whatsapp', e.target.value)}
                  placeholder="(62) 9 0000-0000"
                />
              </div>
            </div>

            <div>
              <label className="label" htmlFor="lgpd-det">Detalhes</label>
              <textarea
                id="lgpd-det"
                className="input min-h-[120px] resize-y"
                value={form.detalhes}
                onChange={(e) => update('detalhes', e.target.value)}
                placeholder="Descreva sua solicitação. Ex.: para o pedido FZxxxxxx, peço a correção do endereço de entrega."
              />
            </div>

            <label className="flex items-start gap-3 text-sm text-ink-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.confirmou}
                onChange={(e) => update('confirmou', e.target.checked)}
                className="mt-0.5 accent-brand-600 h-4 w-4 shrink-0"
              />
              <span>
                Declaro, sob as penas da lei, ser o titular dos dados ou seu
                representante legal. Estou ciente de que a Fretz pode solicitar
                comprovação de identidade antes de atender ao pedido (art. 18,
                § 5º, LGPD).
              </span>
            </label>

            {error && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full gap-2"
            >
              {submitting ? 'Enviando…' : (
                <>
                  Enviar solicitação
                  <PaperAirplaneIcon className="h-4 w-4" />
                </>
              )}
            </button>
          </form>

          <div className="mt-8 text-sm text-ink-500 leading-relaxed">
            <p>
              Prefere outro canal? Escreva diretamente para{' '}
              <a href="mailto:privacidade@fretz.com.br" className="text-brand-700 underline">
                privacidade@fretz.com.br
              </a>{' '}
              informando o e-mail e WhatsApp do cadastro.
            </p>
            <p className="mt-2">
              Se entender que o tratamento dos seus dados é incompatível com a
              LGPD, você também pode reclamar à{' '}
              <a href="https://www.gov.br/anpd" target="_blank" rel="noopener noreferrer"
                 className="text-brand-700 underline">
                ANPD
              </a>{' '}
              <ArrowRightIcon className="inline h-3 w-3" />
            </p>
          </div>
        </div>
      </section>

      <Footer />
      <FooterCopyright />
    </div>
  );
};

export default Lgpd;
